import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RadioButtons, TextInput, Typography } from '@guildeducationinc/recess';
import InfoOutlinedIcon from '@guildeducationinc/recess/icons/InfoOutlinedIcon';
import { Alert } from '@src/components/Alert/Alert';
import { CountrySelect } from '@src/components/CountrySelect';
import { FIELDS_BY_CODE } from '@src/components/Employee/EditProgram/ProgramFields';
import { ProgramPurpose } from '@src/components/Employee/EditProgram/ProgramPurpose';
import { RequestFunding } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/AboutPaymentRequest/RequestFunding';
import { DeadlineErrorMessages } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/DeadlineErrorMessages/DeadlineErrorMessages';
import { FieldOptionsLookup } from '@src/components/FieldOptionsLookup';
import { UploadDocuments } from '@src/components/UploadDocuments';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { useAuth } from '@src/hooks/useAuth';
import { EntityType, OtherOption, ProgramLevelType, RuleType, } from '@src/types';
import { getSchoolPolicy, requireFieldsOfStudy, requirePickListValue, requireProgramName, requireProvider, requireSchool, warnOnOtherFieldOfStudy, warnOnOtherSchool, } from '@src/utilities/benefitsHelper';
import classNames from 'classnames';
import { DatePicker, useLocale } from 'pe-core-frontend';
import { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PreimbursementQuestions } from '../../PaymentRequests/PreimbursementQuestions/PreimbursementQuestions';
import styles from './ProgramStepInput.module.scss';
import { useSchoolCountryManagement } from './useSchoolCountryManagement';
export const ProgramStepInput = ({ program, paymentRequest, title, description, fields, onProgramChange, onPaymentRequestChange, eligibilityLock, errors = {}, helpLink, messages, showRequiresExpenses = false, }) => {
    const { t } = useLocale();
    const { currentUser } = useAuth();
    const { myBenefits, getPolicy, clientProgramLevels } = useContext(ProgramContext);
    const schoolPolicy = useMemo(() => getSchoolPolicy(myBenefits, program), [myBenefits, program]);
    const programPolicy = useMemo(() => getPolicy(RuleType.PROGRAM, program), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [program]);
    const clientProgramLevel = useMemo(() => {
        return clientProgramLevels.find(cpl => cpl.programLevelId === program.programLevelId);
    }, [clientProgramLevels, program.programLevelId]);
    // Check if budget policy is enabled
    const budgetPolicy = useMemo(() => getPolicy(RuleType.BUDGET, program), [getPolicy, program]);
    const hasBudgetPolicy = budgetPolicy && budgetPolicy.approvalMode;
    // Get funding data for RequestFunding component
    const fundingPolicy = useMemo(() => {
        return (myBenefits?.fundings?.find(f => f.program?.id === program.id) || myBenefits?.fundings?.[0]);
    }, [myBenefits?.fundings, program.id]);
    const fundingCap = fundingPolicy?.cap || 0;
    const fundingLimit = fundingPolicy?.remaining || 0;
    const currency = fundingPolicy?.currency || 'USD';
    // Memoize "Other" text to avoid repeated translations
    const otherText = useMemo(() => t('Other'), [t]);
    // Extract all country management logic into a custom hook
    const { countrySelected, countryOptions, handleCountryChange, applyPendingCountryUpdate } = useSchoolCountryManagement({
        program,
        schoolPolicy,
        onProgramChange,
        otherText,
        defaultCountry: currentUser?.clientInfo?.country || 'US',
    });
    // Memoize initial country code for CountrySelect
    const initialCountryCode = useMemo(() => program.otherSchoolCountryCode || program.client?.clientConfig?.country || 'US', [program.client?.clientConfig?.country, program.otherSchoolCountryCode]);
    const handleLookupChange = (field) => (opt) => {
        // Apply any pending country update when user interacts with a different field
        if (field !== 'schoolId' && field !== 'fieldOfStudyId') {
            applyPendingCountryUpdate();
        }
        onProgramChange(prg => {
            const changes = { [field]: opt?.value || '' };
            if (field === 'schoolId') {
                changes.schoolId = Number(opt?.value || 0);
                changes.schoolName = opt?.label || '';
                if (changes.schoolId !== -1) {
                    changes.otherSchool = null;
                }
            }
            else if (field === 'fieldOfStudyId') {
                changes.fieldOfStudyId = Number(opt?.value || 0);
                changes.fieldOfStudyName = opt?.label || '';
                if (changes.fieldOfStudyId !== -1) {
                    changes.otherFieldOfStudy = null;
                }
            }
            else if (field === 'programLevelId') {
                changes.programLevelId = Number(opt?.value || 0);
                changes.programLevelName = opt?.label || '';
                // clear out custom1 when program level changes
                changes.custom1 = '';
                if (opt) {
                    const type = opt.params?.category ?? ProgramLevelType.RECURRING;
                    // set isSingleExpense based on program level type
                    switch (type) {
                        case ProgramLevelType.RECURRING:
                            changes.isSingleExpense = false;
                            break;
                        case ProgramLevelType.SINGLE:
                            changes.isSingleExpense = true;
                            break;
                        default:
                            changes.isSingleExpense = undefined;
                            break;
                    }
                }
            }
            return { ...prg, ...changes };
        });
    };
    const handleDateChange = (field) => (dt) => {
        // Apply any pending country update when user interacts with date field
        applyPendingCountryUpdate();
        onProgramChange(prg => {
            const changes = { [field]: dt };
            if (field === 'startDate' && program.isSingleExpense) {
                // If single expense, set estCompDate to startDate
                changes.estCompDate = changes.startDate;
            }
            return { ...prg, ...changes };
        });
    };
    const handleRequiresExpensesChange = (value) => {
        onPaymentRequestChange(prev => ({
            ...prev,
            requiresExpenses: value,
        }));
    };
    const getFieldValue = (field) => {
        const val = program[field];
        if (!val)
            return '';
        return val.toString();
    };
    const renderFieldInput = (field) => {
        let label = t(field.label);
        let additionalInfo = field.additionalInfo ? t(field.additionalInfo) : undefined;
        let helpText = field.infoText ? t(field.infoText) : '';
        if (field.field !== 'programLevelId' && !program.programLevelId) {
            // render other fields only after program level is selected
            return null;
        }
        if (field.field === 'schoolId') {
            if (!requireSchool(myBenefits, program)) {
                return null;
            }
            if (schoolPolicy?.otherSchool === OtherOption.DISALLOW) {
                helpText = field.alternateInfoText ? t(field.alternateInfoText) : '';
            }
        }
        if (field.field === 'schoolName' && !requireProvider(myBenefits, program)) {
            return null;
        }
        if (field.field === 'fieldOfStudyId') {
            if (!requireFieldsOfStudy(myBenefits, program)) {
                return null;
            }
            if (schoolPolicy?.otherFieldOfStudy === OtherOption.DISALLOW) {
                helpText = field.alternateInfoText ? t(field.alternateInfoText) : '';
                additionalInfo = field.alternateAdditionalInfo
                    ? t(field.alternateAdditionalInfo)
                    : undefined;
            }
        }
        if (field.field === 'description' && requireFieldsOfStudy(myBenefits, program)) {
            return null;
        }
        if (field.field === 'custom1') {
            if (!requirePickListValue(myBenefits, program)) {
                return null;
            }
            label = schoolPolicy?.pickList?.label || label;
            if (schoolPolicy?.pickList?.tooltip) {
                additionalInfo = schoolPolicy?.pickList?.tooltip;
            }
        }
        if (field.field === 'programName' && !requireProgramName(myBenefits, program)) {
            return null;
        }
        if (field.field === 'estCompDate' && program.isSingleExpense) {
            return null;
        }
        if (field.field === 'isSingleExpense') {
            // Ask employee about Program or Single Expense only if program level is both
            if (clientProgramLevel?.type !== ProgramLevelType.BOTH) {
                return null;
            }
            return (_jsx("div", { children: _jsx(RadioButtons, { groupLabel: t('Did you pay for a program or course related to this expense?'), name: 'isSingleExpense', values: [
                        {
                            value: 'false',
                            label: t('Yes, I have paid for a course or program related to this expense and am requesting reimbursement for it.'),
                        },
                        {
                            value: 'true',
                            label: t('No, I did not take a program or course but have expenses related to my education benefit.'),
                        },
                    ], selectedValue: program.isSingleExpense === undefined
                        ? ''
                        : program.isSingleExpense
                            ? 'true'
                            : 'false', onChange: e => onProgramChange(prg => ({ ...prg, isSingleExpense: e.target.value === 'true' })), errors: errors[field.field], helpText: helpText, required: field.required, additionalInfo: additionalInfo }) }));
        }
        if (field.field === 'startDate') {
            if (program.isSingleExpense) {
                label = programPolicy?.singleExpenseDate?.label ?? t('Purchase date');
                helpText = programPolicy?.singleExpenseDate?.description ?? '';
                additionalInfo = programPolicy?.singleExpenseDate?.tooltip ?? '';
            }
        }
        if (field.lookupCode) {
            return (_jsx(FieldOptionsLookup, { clientId: program.clientId, entityType: EntityType.PROGRAM, fieldCode: field.lookupCode, label: label, value: getFieldValue(field.field), onChange: handleLookupChange(field.field), errors: errors[field.field], showAllAtOnce: field.showAllAtOnce, helpText: helpText, required: field.required, additionalInfo: additionalInfo, showSearchIcon: field.showSearchIcon, pageSize: field.field === 'schoolId' ? 2000 : 500 }, field.field));
        }
        if (field.field === 'estCompDate' || field.field === 'startDate') {
            return (_jsx(DatePicker, { label: `${label} *`, value: program[field.field] || '', onChange: handleDateChange(field.field), errors: errors[field.field], helpText: helpText, additionalInfo: additionalInfo }));
        }
        if (field.field === 'purpose') {
            return (_jsx(ProgramPurpose, { clientId: program.clientId, purpose: program.purpose, onChange: purpose => onProgramChange(prg => ({ ...prg, purpose })), fieldInfo: field, errors: errors[field.field] }));
        }
        if (field.field === 'documents') {
            return (_jsx(UploadDocuments, { infoText: helpText, explanation: t(field.description), documents: program.documents.docs, onChange: documents => onProgramChange(prg => ({ ...prg, documents: { docs: documents } })) }));
        }
        return (_jsx(TextInput, { label: label, required: field.required, value: getFieldValue(field.field), onChange: e => {
                // Apply any pending country update when user types in text field
                applyPendingCountryUpdate();
                onProgramChange(prg => ({ ...prg, [field.field]: e.target.value || '' }));
            }, errors: errors[field.field], helpText: helpText, additionalInfo: additionalInfo }));
    };
    const renderOtherFieldInput = (field) => {
        if (field.field === 'schoolId' &&
            program.schoolId === -1 &&
            requireSchool(myBenefits, program)) {
            const otherField = FIELDS_BY_CODE.otherSchool;
            if (otherField) {
                return (_jsxs("div", { className: styles.schoolFieldsGrid, children: [_jsx("div", { className: styles.fieldGroup, children: renderFieldInput(otherField) }), _jsxs("div", { className: styles.fieldGroup, children: [_jsx(Typography, { variant: 'label1', children: t('Other School Country *') }), _jsx(CountrySelect, { className: 'margin--top_16', value: countrySelected?.code ?? initialCountryCode, onChange: country => {
                                        handleCountryChange(country);
                                    }, options: countryOptions, errors: errors.otherSchoolCountryCode, supportEmptyState: false })] })] }));
            }
        }
        if (field.field === 'fieldOfStudyId' &&
            program.fieldOfStudyId === -1 &&
            requireFieldsOfStudy(myBenefits, program)) {
            const otherField = FIELDS_BY_CODE.otherFieldOfStudy;
            if (otherField) {
                return renderFieldInput(otherField);
            }
        }
        return null;
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: styles.heading, children: [_jsx(Typography, { variant: 'title2', children: title }), _jsxs("div", { className: styles.description, children: [description && _jsx(Typography, { variant: 'body1', children: description }), helpLink && (_jsx("div", { children: _jsxs(Link, { to: helpLink, className: styles.helpLink, target: '_blank', rel: 'noopener noreferrer', children: [_jsx(InfoOutlinedIcon, { size: 24 }), _jsxs(Typography, { variant: 'body1', children: [" ", t("Learn more about what's covered")] })] }) }))] }), _jsx(Typography, { variant: 'label2', children: t('* Required') })] }), _jsx("div", { className: styles.fields, children: fields.filter(renderFieldInput).map(field => (_jsxs("div", { children: [field.field === 'startDate' &&
                            program.programLevelId &&
                            program.isSingleExpense &&
                            !paymentRequest.id && // Only show preimbursement questions for new payment requests
                            eligibilityLock.preimbursement && (_jsx(PreimbursementQuestions, { className: 'mb-4', program: program, paymentRequest: paymentRequest, onChange: onPaymentRequestChange, requestErrors: errors })), _jsxs("div", { className: classNames(!['purpose', 'isSingleExpense'].includes(field.field) && styles.fieldRow), children: [renderFieldInput(field), renderOtherFieldInput(field)] }, field.field), field.field === 'schoolId' &&
                            program.schoolId === -1 &&
                            warnOnOtherSchool(myBenefits, program) && (_jsx("div", { className: 'mt-4', children: _jsx(Alert, { type: 'warning', message: t('Other school warning') }) })), field.field === 'fieldOfStudyId' &&
                            program.fieldOfStudyId === -1 &&
                            warnOnOtherFieldOfStudy(myBenefits, program) && (_jsx("div", { className: 'mt-4', children: _jsx(Alert, { type: 'warning', message: t('Other field of study warning') }) })), field.field === 'startDate' && messages.length > 0 && (_jsx("div", { className: 'mt-4', children: _jsx(DeadlineErrorMessages, { messages: messages }) })), field.field === 'startDate' && program.isSingleExpense && (_jsxs("div", { className: 'mt-4', children: [showRequiresExpenses && (_jsx(RadioButtons, { groupLabel: `${t('Have you already paid for these expenses?')} *`, name: 'requiresExpenses', onChange: e => handleRequiresExpensesChange(e.target.value === 'true'), selectedValue: String(paymentRequest.requiresExpenses ?? false), values: [
                                        {
                                            label: t('Yes, I have already paid'),
                                            value: 'true',
                                        },
                                        {
                                            label: t('No, I have not paid yet'),
                                            value: 'false',
                                        },
                                    ] })), paymentRequest.requiresExpenses === false && hasBudgetPolicy && (_jsx(RequestFunding, { paymentRequest: paymentRequest, onChange: onPaymentRequestChange, fundingCap: fundingCap, fundingLimit: fundingLimit, currency: currency, clientId: paymentRequest.clientId, errors: errors }))] }))] }, field.field))) })] }));
};
